import React, { ReactNode } from "react";
import styles from "./GradiantButton.module.scss";
import { Typography } from "top-brains-ui";
import classNames from "classnames";
import arrow from "@src/assets/arrow-narrow-right.svg";
import signInIcon from "@src/assets/signin_icon.svg";

interface GradiantButtonProps {
  label: string;
  img?: string | ReactNode;
  onClick?: () => void;
  className?: string;
  width?: string;
  height?: string;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "tertiary";
  disabled?: boolean;
  fontSize?: number;
  labelClassName?: string;
  isRightArrow?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
}

const GradiantButton: React.FC<GradiantButtonProps> = ({
  label,
  img,
  onClick,
  width,
  height,
  className = "",
  labelClassName = "",
  type = "button",
  variant = "primary",
  disabled = false,
  fontSize = 14,
  isRightArrow = false,
  isLoading = false,
  loadingLabel,
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case "primary":
        return styles.primaryVariant;
      case "secondary":
        return styles.secondaryVariant;
      case "tertiary":
        return styles.tertiaryVariant;
      default:
        return styles.primaryVariant;
    }
  };

  const isButtonDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      disabled={isButtonDisabled}
      className={classNames(getVariantClass(), className, {
        [styles.gradientButtonDisabled]: isButtonDisabled,
      })}
      onClick={(e) => {
        if (isButtonDisabled) {
          e.preventDefault();
          return;
        }
        if (onClick) onClick();
      }}
      style={{
        width,
        minWidth: "100px",
        height,
        cursor: isButtonDisabled ? "not-allowed" : "pointer",
      }}
    >
      {isLoading ? (
        <span className={styles.loadingContainer}>
          <span className={styles.spinner} />
          <Typography
            fontWeight="regular"
            fontSize={fontSize}
            className={labelClassName}
          >
            {loadingLabel || label}
          </Typography>
        </span>
      ) : (
        <>
          {/* Tertiary variant (special case) */}
          {variant === "tertiary" && (
            <>
              <img src={signInIcon} alt="signin" className={styles.icon} />
              <Typography
                fontWeight="regular"
                fontSize={fontSize}
                className={styles.label}
              >
                {label}
              </Typography>
            </>
          )}

          {/* Non-tertiary with optional icon */}
          {variant !== "tertiary" && img && (
            <>
              {typeof img === "string" ? (
                <img src={img} alt="icon" className={styles.icon} />
              ) : (
                <span className={styles.icon}>{img}</span>
              )}
            </>
          )}

          {/* Label */}
          {variant !== "tertiary" && (
            <Typography
              fontWeight="regular"
              fontSize={fontSize}
              className={labelClassName}
            >
              {label}
            </Typography>
          )}

          {/* Right arrow */}
          {variant !== "tertiary" && isRightArrow && (
            <img
              src={arrow}
              alt="arrow"
              className={`${styles.icon} ${styles.rightArrow}`}
            />
          )}
        </>
      )}
    </button>
  );
};

export default GradiantButton;
